// Importing necessary modules
import { jwtDecode } from "jwt-decode";

// A utility function for handling API requests with loading, success, and error handling
export const requestHandler = async (api, setLoading, onSuccess, onError) => {
	// Show loading state if setLoading function is provided
	if (setLoading) setLoading(true);
	try {
		// Make the API request
		const response = await api();
		const { data } = response;
		if (data?.statusCode < 400) {
			// Call the onSuccess callback with the response data
			onSuccess(data);
		}
	} catch (error) {
		// Handle error cases, including unauthorized and forbidden cases
		if ([403].includes(error?.response?.data?.statusCode)) {
			localStorage.clear(); // Clear local storage on authentication issues
			if (isBrowser) window.location.href = "/"; // Redirect to login page
		}
		// for Input Type validation errors
		if (422 === error?.response?.data?.statusCode) {
			const keys = Object.keys(error?.response?.data?.error);
			const errorArray = error?.response?.data?.error[keys[0]];

			onError(
				errorArray[0] ||
					error?.response?.data?.message ||
					"Something went wrong"
			);
		} else {
			onError(error?.response?.data?.message || "Something went wrong");
		}
	} finally {
		// Hide loading state if setLoading function is provided
		if (setLoading) setLoading(false);
	}
};

// Check if the code is running in a browser environment
export const isBrowser = typeof window !== "undefined";

// A class that provides utility functions for working with local storage
export class LocalStorage {
	// Get a value from local storage by key
	static get(key) {
		if (!isBrowser) return;
		const value = localStorage.getItem(key);
		if (value) {
			try {
				return JSON.parse(value);
			} catch (err) {
				return null;
			}
		}
		return null;
	}

	// Set a value in local storage by key
	static set(key, value) {
		if (!isBrowser) return;
		localStorage.setItem(key, JSON.stringify(value));
	}

	// Remove a value from local storage by key
	static remove(key) {
		if (!isBrowser) return;
		localStorage.removeItem(key);
	}

	// Clear all items from local storage
	static clear() {
		if (!isBrowser) return;
		localStorage.clear();
	}
}

// Function to check if the token is expired
export const checkTokenExpiry = (token) => {
	if (!token) return true; // Consider expired if token is missing

	try {
		// Ensure the token is decoded and its expiration time is extracted
		const decodedToken = jwtDecode(token);
		const currentTime = Date.now() / 1000;

		// Return true if the token has expired
		return decodedToken.exp < currentTime;
	} catch (error) {
		// If there's an error decoding the token, assume it's expired
		return true;
	}
};
